/**
 * Conversion Trend Chart Component
 * Multi-line chart showing leads, conversions, and conversion rate trends
 * Phase 6.2 - Performance Charts
 */

import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, Typography, Skeleton } from '@mui/material';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export interface TrendDataPoint {
  month: string;
  newLeads: number;
  conversions: number;
  conversionRate: number;
}

export interface ConversionTrendChartProps {
  data: TrendDataPoint[];
  loading?: boolean;
}

const ConversionTrendChart: React.FC<ConversionTrendChartProps> = ({
  data,
  loading = false
}) => {
  // Prepare chart data
  const chartData = {
    labels: data.map(d => d.month),
    datasets: [
      {
        label: 'New Leads',
        data: data.map(d => d.newLeads),
        borderColor: 'rgba(25, 118, 210, 1)',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: 'rgba(25, 118, 210, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        fill: true,
        yAxisID: 'y'
      },
      {
        label: 'Conversions',
        data: data.map(d => d.conversions),
        borderColor: 'rgba(46, 125, 50, 1)',
        backgroundColor: 'rgba(46, 125, 50, 0.1)',
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: 'rgba(46, 125, 50, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        fill: true,
        yAxisID: 'y'
      },
      {
        label: 'Conversion Rate (%)',
        data: data.map(d => d.conversionRate),
        borderColor: 'rgba(237, 108, 2, 1)',
        backgroundColor: 'rgba(237, 108, 2, 0.1)',
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: 'rgba(237, 108, 2, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        fill: false,
        yAxisID: 'y1',
        borderDash: [5, 5]
      }
    ]
  };

  // Chart options
  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 13,
            weight: '500'
          }
        }
      },
      title: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            if (label.includes('Rate')) {
              return `${label}: ${value.toFixed(1)}%`;
            }
            return `${label}: ${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          }
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        title: {
          display: true,
          text: 'Leads / Conversions',
          font: {
            size: 12,
            weight: '600'
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 12
          }
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        title: {
          display: true,
          text: 'Conversion Rate (%)',
          font: {
            size: 12,
            weight: '600'
          }
        },
        grid: {
          drawOnChartArea: false
        },
        ticks: {
          font: {
            size: 12
          },
          callback: (value) => `${value}%`
        }
      }
    }
  };

  return (
    <Box>
      {/* Header */}
      <Typography variant="h6" fontWeight="600" mb={3}>
        Monthly Conversion Trend
      </Typography>

      {loading ? (
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      ) : (
        <Box height={400}>
          <Line data={chartData} options={options} />
        </Box>
      )}

      {/* Summary Stats */}
      <Box display="flex" gap={4} mt={3} justifyContent="center" flexWrap="wrap">
        <Box textAlign="center">
          <Typography variant="caption" color="text.secondary" display="block">
            Total New Leads
          </Typography>
          <Typography variant="h6" fontWeight="700" color="primary">
            {data.reduce((sum, d) => sum + d.newLeads, 0).toLocaleString()}
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="caption" color="text.secondary" display="block">
            Total Conversions
          </Typography>
          <Typography variant="h6" fontWeight="700" sx={{ color: '#2e7d32' }}>
            {data.reduce((sum, d) => sum + d.conversions, 0).toLocaleString()}
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="caption" color="text.secondary" display="block">
            Average Conversion Rate
          </Typography>
          <Typography variant="h6" fontWeight="700" sx={{ color: '#ed6c02' }}>
            {(data.reduce((sum, d) => sum + d.conversionRate, 0) / data.length).toFixed(1)}%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ConversionTrendChart;
